import { useCallback, useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { api, brl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SignaturePad from "../components/SignaturePad";
import { toast } from "sonner";
import { CheckCircle2, FileDown, CreditCard, ShieldCheck, QrCode, Receipt, Copy, Upload, Clock } from "lucide-react";

export default function PublicContract() {
  const { token } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [signer, setSigner] = useState("");
  const [email, setEmail] = useState("");
  const [sig, setSig] = useState("");
  const [providerSig, setProviderSig] = useState("");
  const [providerName, setProviderName] = useState("");
  const [pix, setPix] = useState(null);
  const [payerName, setPayerName] = useState("");
  const [receiptDataUrl, setReceiptDataUrl] = useState("");
  const [receiptFilename, setReceiptFilename] = useState("");
  const [submittingPix, setSubmittingPix] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/public/contracts/${token}`);
    setData(r.data);
    setSigner(r.data.proposal?.client_name || "");
    setEmail(r.data.proposal?.client_email || "");
    setPayerName(r.data.proposal?.client_name || "");
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const sign = async (role) => {
    const url = role === "client" ? sig : providerSig;
    const name = role === "client" ? signer : providerName;
    if (!url) return toast.error("Assine para continuar");
    await api.post(`/public/contracts/${token}/sign`, {
      role, signer_name: name, signer_email: email, signature_data_url: url,
    });
    toast.success(role === "client" ? "Assinatura registrada" : "Contrato finalizado");
    load();
  };

  const loadPix = async () => {
    if (pix) return;
    try {
      const r = await api.get(`/public/proposals/${proposal.public_token}/pix`);
      setPix(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível carregar dados do Pix");
    }
  };

  const onReceiptFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Arquivo grande demais (máx 5MB)");
    const reader = new FileReader();
    reader.onload = () => { setReceiptDataUrl(reader.result); setReceiptFilename(f.name); };
    reader.readAsDataURL(f);
  };

  const submitPix = async () => {
    if (!receiptDataUrl) return toast.error("Anexe o comprovante");
    if (submittingPix) return;
    setSubmittingPix(true);
    try {
      await api.post(`/public/proposals/${proposal.public_token}/pix-submit`, { payer_name: payerName, receipt_data_url: receiptDataUrl });
      toast.success("Comprovante enviado! Aguardando confirmação do prestador.");
      setPixOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar");
    } finally {
      setSubmittingPix(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center overline">Carregando…</div>;
  const brand = data.brand || {};
  const contract = data.contract;
  const proposal = data.proposal || {};
  const primary = brand.brand_primary || "#C07C88";
  const gold = brand.brand_accent || "#D4AF37";
  const paymentLink = location.state?.payment_link || proposal?.payment_link;
  const paymentStatus = proposal?.payment_status || "unpaid";
  const downPayment = Math.round((proposal?.totals?.total || 0) * 0.3 * 100) / 100;

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-10 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Payment status banner */}
        {paymentStatus === "paid" ? (
          <Card className="p-5 mb-5 border-[#10B981] shadow-none bg-emerald-50">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-emerald-600" />
              <div>
                <p className="overline text-emerald-700">Sinal Pago · {brl(downPayment)}</p>
                <p className="text-sm mt-1">Pagamento confirmado pelo prestador. Projeto liberado para execução.</p>
              </div>
            </div>
          </Card>
        ) : paymentStatus === "pending_review" ? (
          <Card className="p-5 mb-5 border-[#F59E0B] shadow-none bg-amber-50">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-amber-600" />
              <div>
                <p className="overline text-amber-700">Comprovante em análise</p>
                <p className="text-sm mt-1">Seu comprovante foi enviado. Aguarde a confirmação do prestador.</p>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-5 mb-5 border-[#D4AF37] shadow-none bg-[#FDF9EE]">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="overline" style={{ color: gold }}>Sinal 30% · {brl(downPayment)}</p>
                <p className="text-sm mt-1">Escolha como pagar o sinal para liberar o início do projeto.</p>
              </div>
            </div>
            <Tabs defaultValue="card" className="mt-4">
              <TabsList className="bg-white border border-border">
                <TabsTrigger value="card" data-testid="pay-tab-card"><CreditCard size={14} className="mr-1" /> Cartão</TabsTrigger>
                <TabsTrigger value="boleto" data-testid="pay-tab-boleto"><Receipt size={14} className="mr-1" /> Boleto</TabsTrigger>
                <TabsTrigger value="pix" onClick={loadPix} data-testid="pay-tab-pix"><QrCode size={14} className="mr-1" /> Pix</TabsTrigger>
              </TabsList>
              <TabsContent value="card">
                <p className="text-xs text-muted-foreground mt-3">Pagamento seguro via Stripe. Aprovação imediata.</p>
                {paymentLink ? (
                  <Button asChild className="mt-3" style={{ background: gold, color: "#fff" }} data-testid="pay-card-btn">
                    <a href={paymentLink} target="_blank" rel="noreferrer"><CreditCard size={14} className="mr-2" /> Pagar com cartão</a>
                  </Button>
                ) : <p className="text-xs text-red-500 mt-2">Link de pagamento indisponível.</p>}
              </TabsContent>
              <TabsContent value="boleto">
                <p className="text-xs text-muted-foreground mt-3">Boleto bancário — compensação em até 3 dias úteis.</p>
                {paymentLink ? (
                  <Button asChild className="mt-3" style={{ background: gold, color: "#fff" }} data-testid="pay-boleto-btn">
                    <a href={paymentLink} target="_blank" rel="noreferrer"><Receipt size={14} className="mr-2" /> Gerar boleto</a>
                  </Button>
                ) : <p className="text-xs text-red-500 mt-2">Indisponível.</p>}
              </TabsContent>
              <TabsContent value="pix">
                {pix ? (
                  <div className="grid md:grid-cols-2 gap-4 mt-3">
                    <div className="text-center bg-white p-3 rounded-md border border-border">
                      <img src={`data:image/png;base64,${pix.qr_png_base64}`} alt="QR Pix" className="w-full max-w-[220px] mx-auto" data-testid="pix-qr" />
                      <p className="text-xs font-heading mt-2" style={{ color: gold }}>{brl(pix.amount)}</p>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Chave Pix ({pix.pix_key_type})</Label>
                        <div className="flex gap-2">
                          <Input value={pix.pix_key} readOnly className="text-xs" data-testid="pix-key" />
                          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(pix.pix_key); toast.success("Chave copiada"); }}><Copy size={13} /></Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Pix Copia e Cola</Label>
                        <div className="flex gap-2">
                          <Input value={pix.brcode} readOnly className="text-[10px] font-mono" />
                          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(pix.brcode); toast.success("Código copiado"); }} data-testid="copy-brcode"><Copy size={13} /></Button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Beneficiário: <b>{pix.beneficiary}</b></p>
                      <Dialog open={pixOpen} onOpenChange={setPixOpen}>
                        <DialogTrigger asChild>
                          <Button className="w-full mt-2" style={{ background: gold, color: "#fff" }} data-testid="upload-receipt-btn"><Upload size={14} className="mr-1" /> Já paguei · enviar comprovante</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Enviar comprovante Pix</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <div><Label>Seu nome</Label><Input value={payerName} onChange={(e) => setPayerName(e.target.value)} data-testid="payer-name" /></div>
                            <div>
                              <Label>Comprovante (imagem ou PDF)</Label>
                              <Input type="file" accept="image/*,application/pdf" onChange={onReceiptFile} data-testid="receipt-file" />
                              {receiptFilename && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 size={12} /> {receiptFilename}</p>}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-start gap-2"><ShieldCheck size={14} className="shrink-0 mt-0.5" />Registro de IP, dispositivo e data/hora do envio.</div>
                          </div>
                          <DialogFooter>
                            <Button style={{ background: gold, color: "#fff" }} onClick={submitPix} disabled={submittingPix} data-testid="submit-receipt-btn">
                              {submittingPix ? "Enviando…" : "Enviar comprovante"}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-3">Clique na aba Pix para gerar o QR.</p>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        )}

        <div className="doc-paper p-10 md:p-14">
          <div className="text-center pb-6 border-b border-border">
            <p className="overline">Contrato</p>
            <h1 className="font-heading text-3xl mt-2" style={{ color: primary }}>{contract.title}</h1>
            <p className="text-xs text-muted-foreground mt-2">{brand.company_name}</p>
          </div>
          <div className="prose prose-sm max-w-none mt-8 whitespace-pre-wrap text-sm leading-relaxed text-[#1A1A1A]" data-testid="contract-body">
            {contract.body}
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-10 pt-8 border-t border-border">
            <div>
              <p className="overline mb-3">CONTRATANTE</p>
              {contract.client_signature ? (
                <div className="doc-paper p-4">
                  <img src={contract.client_signature.signature_data_url} alt="Assinatura cliente" className="h-16 mx-auto" />
                  <p className="text-xs mt-2 text-center">{contract.client_signature.signer_name}</p>
                  <p className="text-[10px] text-muted-foreground text-center">IP: {contract.client_signature.ip} · {contract.client_signature.at?.slice(0,19).replace("T"," ")}</p>
                  <div className="text-center mt-2"><span className="text-[10px] px-2 py-1 rounded-full" style={{ background: "#D4AF3720", color: gold }}>ASSINADO</span></div>
                </div>
              ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="w-full" style={{ background: primary, color: "#fff" }} data-testid="sign-client-btn">
                      <CheckCircle2 size={16} className="mr-2" /> Assinar como cliente
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Assinatura do CONTRATANTE</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label className="text-xs">Nome</Label><Input value={signer} onChange={(e) => setSigner(e.target.value)} data-testid="client-signer-name" /></div>
                      <div><Label className="text-xs">E-mail</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="client-signer-email" /></div>
                      <SignaturePad onChange={setSig} testid="client-signature-pad" />
                      <div className="text-xs text-muted-foreground flex items-start gap-2"><ShieldCheck size={14} className="shrink-0 mt-0.5" />Registro de IP, dispositivo, data/hora e navegador.</div>
                    </div>
                    <DialogFooter><Button style={{ background: primary, color: "#fff" }} onClick={() => sign("client")} data-testid="confirm-sign-client">Confirmar assinatura</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div>
              <p className="overline mb-3">CONTRATADA</p>
              {contract.provider_signature ? (
                <div className="doc-paper p-4">
                  <img src={contract.provider_signature.signature_data_url} alt="Assinatura prestador" className="h-16 mx-auto" />
                  <p className="text-xs mt-2 text-center">{contract.provider_signature.signer_name}</p>
                  <p className="text-[10px] text-muted-foreground text-center">IP: {contract.provider_signature.ip} · {contract.provider_signature.at?.slice(0,19).replace("T"," ")}</p>
                  <div className="text-center mt-2"><span className="text-[10px] px-2 py-1 rounded-full" style={{ background: "#D4AF3720", color: gold }}>ASSINADO</span></div>
                </div>
              ) : contract.client_signature ? (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="sign-provider-btn"><CheckCircle2 size={16} className="mr-2" /> Assinar como prestador</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Assinatura da CONTRATADA</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label className="text-xs">Nome do responsável</Label><Input value={providerName} onChange={(e) => setProviderName(e.target.value)} placeholder={brand.company_name} data-testid="provider-signer-name" /></div>
                      <SignaturePad onChange={setProviderSig} testid="provider-signature-pad" />
                    </div>
                    <DialogFooter><Button style={{ background: gold, color: "#fff" }} onClick={() => sign("provider")} data-testid="confirm-sign-provider">Finalizar contrato</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              ) : (
                <p className="text-xs text-muted-foreground py-4 text-center">Aguardando assinatura do cliente</p>
              )}
            </div>
          </div>

          {contract.status === "fully_signed" && (
            <div className="text-center mt-8 pt-8 border-t border-border">
              <p className="font-heading text-2xl" style={{ color: gold }}>✧ CONTRATO FINALIZADO ✧</p>
              <p className="text-xs text-muted-foreground mt-2">Trilha de auditoria completa. Baixe a cópia PDF abaixo.</p>
            </div>
          )}

          <div className="text-center mt-6">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/contracts/${token}/pdf`} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-[#C07C88] inline-flex items-center gap-1" data-testid="contract-pdf-link">
              <FileDown size={12} /> Baixar PDF do contrato
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
